import type { AssetAudioInfo } from '../../domain/schemas';
import { MAX_SAMPLE_SECONDS } from '../../domain/schemas';

/**
 * Decoded audio for samples and clips, shared by live playback, export, previews and
 * waveform views.
 *
 * Memory rules (decoded audio is ~10× larger than the file):
 *  - decoding is lazy (only when something needs to play or draw the asset);
 *  - every buffer is resampled to one rate (44.1 kHz) on decode and counted at its real
 *    size (frames × channels × 4 bytes); the total is held under `budgetBytes` by
 *    evicting least-recently-used entries;
 *  - reversed variants are created on demand and count against the same budget;
 *  - concurrent requests for one asset share a single decode; at most `concurrency`
 *    decodes run at once;
 *  - `acquire()` loads a set of assets that must all be resident together (playback,
 *    export) and fails explicitly when they cannot fit, instead of thrashing.
 *
 * Pinning is reference-counted. Each successful `acquire()` returns its own handle that
 * owns one reference per entry it pinned; an entry stays pinned until every handle
 * holding it has been released, so playback and export can share a decoded asset and
 * release in any order. `release()` is idempotent. `clear()` starts a new generation:
 * pin counts are reset, handles from before the reset become no-ops, and decodes that
 * were in flight can no longer write into the cache or its bookkeeping.
 */

export const DECODE_SAMPLE_RATE = 44100;
export const DEFAULT_DECODE_BUDGET = 96 * 1024 * 1024;

/** 'reset': the cache was cleared repeatedly while this acquisition was loading. */
export type AssetErrorCode = 'missing' | 'decode' | 'too-long' | 'budget' | 'reset';

export class AssetLoadError extends Error {
  readonly code: AssetErrorCode;
  readonly assetId: string;
  constructor(code: AssetErrorCode, assetId: string, message: string) {
    super(message);
    this.name = 'AssetLoadError';
    this.code = code;
    this.assetId = assetId;
  }
}

export interface DecodedBuffer {
  readonly numberOfChannels: number;
  readonly length: number;
  readonly sampleRate: number;
  readonly duration: number;
  getChannelData(channel: number): Float32Array;
}

export interface CacheDeps<B extends DecodedBuffer = AudioBuffer> {
  /** Reads the stored file; undefined when the binary is missing. */
  load(assetId: string): Promise<{ data: ArrayBuffer; mimeType: string } | undefined>;
  decode(data: ArrayBuffer): Promise<B>;
  /** Creates an empty buffer of the same shape (used for reversed variants). */
  create(channels: number, length: number, sampleRate: number): B;
}

interface Entry<B> {
  buffer: B;
  bytes: number;
}

export interface CacheStats {
  entries: number;
  bytes: number;
  budgetBytes: number;
  decodes: number;
  hits: number;
  evictions: number;
  inFlight: number;
  /** Entries currently pinned by at least one acquisition. */
  pinned: number;
  /** Total references held by live acquisition handles. */
  pinRefs: number;
}

export interface PinHandle {
  /** Drops this handle's references. Safe to call more than once. */
  release(): void;
}

type Wanted = { assetId: string; reversed: boolean };

/** How many times an acquisition restarts after a concurrent `clear()` before giving up. */
const MAX_ACQUIRE_ATTEMPTS = 3;

const bytesOf = (b: DecodedBuffer) => b.length * b.numberOfChannels * 4;
const key = (assetId: string, reversed: boolean) => (reversed ? `${assetId}:rev` : assetId);

export class AudioBufferCache<B extends DecodedBuffer = AudioBuffer> {
  private readonly entries = new Map<string, Entry<B>>(); // insertion order = LRU order
  private readonly pending = new Map<string, Promise<B>>();
  private readonly errors = new Map<string, AssetLoadError>();
  /** Reference counts of pinned entry keys for the current generation. */
  private readonly pins = new Map<string, number>();
  private readonly waiters: (() => void)[] = [];
  private running = 0;
  private bytes = 0;
  private counters = { decodes: 0, hits: 0, evictions: 0 };
  private generation = 0;

  constructor(
    private readonly deps: CacheDeps<B>,
    readonly budgetBytes = DEFAULT_DECODE_BUDGET,
    private readonly concurrency = 2,
  ) {}

  /** Synchronous lookup for the audio thread's scheduling callbacks. */
  peek(assetId: string, reversed = false): B | undefined {
    const k = key(assetId, reversed);
    const entry = this.entries.get(k);
    if (!entry) return undefined;
    this.touch(k, entry);
    return entry.buffer;
  }

  /** Last load error for an asset (cleared by `invalidate`). */
  errorFor(assetId: string): AssetLoadError | undefined {
    return this.errors.get(assetId);
  }

  async get(assetId: string, reversed = false): Promise<B> {
    const hit = this.peek(assetId, reversed);
    if (hit) {
      this.counters.hits++;
      return hit;
    }
    if (!reversed) return this.decodeOnce(assetId);
    const generation = this.generation;
    const forward = this.peek(assetId) ?? (await this.decodeOnce(assetId));
    const again = this.peek(assetId, true);
    if (again) return again;
    const rev = this.reverse(forward);
    // A reset while decoding means this result belongs to the old cache.
    if (generation === this.generation) this.store(key(assetId, true), rev);
    return rev;
  }

  /**
   * Loads everything in `wanted` and pins it until the returned handle is released.
   * Throws an AssetLoadError for the first asset that is missing or undecodable, or
   * 'budget' when the pinned audio (this set plus whatever other handles hold) would
   * exceed the decode budget. On failure only this acquisition's references are dropped.
   */
  async acquire(wanted: Iterable<Wanted>): Promise<PinHandle> {
    const items = new Map<string, Wanted>();
    for (const w of wanted) items.set(key(w.assetId, w.reversed), w);
    for (let attempt = 1; ; attempt++) {
      const generation = this.generation;
      const handle = this.pinKeys([...items.keys()], generation);
      try {
        // Pinned before loading, so loading a later item can't evict an earlier one.
        for (const w of items.values()) await this.get(w.assetId, w.reversed);
      } catch (error) {
        handle.release();
        if (generation === this.generation) throw error;
      }
      if (generation !== this.generation) {
        // clear() ran while loading: the old references are void and the buffers were
        // not kept. Start over in the new generation rather than return an empty pin.
        handle.release();
        if (attempt >= MAX_ACQUIRE_ATTEMPTS) {
          const first = [...items.values()][0];
          throw new AssetLoadError('reset', first?.assetId ?? '', 'the audio cache was reset while loading. Try again.');
        }
        continue;
      }
      // Pinned entries can't be evicted, but invalidate() (file deleted) can remove one.
      const missing = [...items.entries()].find(([k]) => !this.entries.has(k));
      if (missing) {
        handle.release();
        throw new AssetLoadError('missing', missing[1].assetId, 'it was removed while loading.');
      }
      const pinnedBytes = this.pinnedBytes();
      if (pinnedBytes > this.budgetBytes) {
        handle.release();
        const first = [...items.values()][0]!;
        throw new AssetLoadError(
          'budget',
          first.assetId,
          `The audio used here needs ${(pinnedBytes / 1048576).toFixed(0)} MB of memory once decoded; the limit is ${(this.budgetBytes / 1048576).toFixed(0)} MB. Use shorter clips or fewer files.`,
        );
      }
      return handle;
    }
  }

  /** Takes one reference on each key; the handle owns exactly these references. */
  private pinKeys(keys: readonly string[], generation: number): PinHandle {
    for (const k of keys) this.pins.set(k, (this.pins.get(k) ?? 0) + 1);
    let released = false;
    return {
      release: () => {
        if (released) return;
        released = true;
        // References from before a clear() were already dropped with the old counts.
        if (generation !== this.generation) return;
        for (const k of keys) {
          const count = (this.pins.get(k) ?? 0) - 1;
          if (count > 0) this.pins.set(k, count);
          else this.pins.delete(k);
        }
        this.evict();
      },
    };
  }

  private pinnedBytes(): number {
    let total = 0;
    for (const k of this.pins.keys()) total += this.entries.get(k)?.bytes ?? 0;
    return total;
  }

  /**
   * Decodes a file that is not stored yet (an import or a new recording) and caches it
   * under `assetId`, so analysis and the first playback share one decode.
   */
  async adopt(assetId: string, data: ArrayBuffer): Promise<B> {
    const generation = this.generation;
    return this.withSlot(async () => {
      let buffer: B;
      try {
        buffer = await this.deps.decode(data.slice(0));
      } catch {
        throw new AssetLoadError('decode', assetId, 'the file could not be decoded. It may be damaged, or this browser cannot decode this format.');
      }
      this.counters.decodes++;
      if (buffer.duration > MAX_SAMPLE_SECONDS + 0.5) {
        throw new AssetLoadError('too-long', assetId, `it is ${Math.round(buffer.duration)} s long; samples are limited to ${MAX_SAMPLE_SECONDS} s.`);
      }
      if (buffer.length === 0) throw new AssetLoadError('decode', assetId, 'the file contains no audio.');
      if (generation === this.generation) this.store(assetId, buffer);
      return buffer;
    });
  }

  /** Drops cached audio (and errors) for an asset, e.g. after deletion. */
  invalidate(assetId: string): void {
    for (const k of [assetId, key(assetId, true)]) {
      const entry = this.entries.get(k);
      if (entry) {
        this.entries.delete(k);
        this.bytes -= entry.bytes;
      }
    }
    this.pending.delete(assetId);
    this.errors.delete(assetId);
  }

  /**
   * Drops everything and starts a new generation. Outstanding handles become no-ops,
   * acquisitions still loading restart, and in-flight decodes finish without touching
   * the new cache.
   */
  clear(): void {
    this.generation++;
    this.entries.clear();
    this.pending.clear();
    this.errors.clear();
    this.pins.clear();
    this.bytes = 0;
  }

  stats(): CacheStats {
    let pinRefs = 0;
    for (const n of this.pins.values()) pinRefs += n;
    return {
      entries: this.entries.size,
      bytes: this.bytes,
      budgetBytes: this.budgetBytes,
      ...this.counters,
      inFlight: this.pending.size,
      pinned: this.pins.size,
      pinRefs,
    };
  }

  private decodeOnce(assetId: string): Promise<B> {
    const existing = this.pending.get(assetId);
    if (existing) return existing;
    const generation = this.generation;
    const promise = this.withSlot(async () => {
      const file = await this.deps.load(assetId);
      if (!file) throw new AssetLoadError('missing', assetId, 'its data is missing from this browser’s storage.');
      let buffer: B;
      try {
        // decodeAudioData detaches its input; keep the stored copy intact.
        buffer = await this.deps.decode(file.data.slice(0));
      } catch {
        const kind = file.mimeType.split('/')[1]?.toUpperCase() ?? 'audio';
        throw new AssetLoadError('decode', assetId, `the file could not be decoded. It may be damaged, or this browser cannot decode ${kind}.`);
      }
      this.counters.decodes++;
      if (buffer.duration > MAX_SAMPLE_SECONDS + 0.5) {
        throw new AssetLoadError('too-long', assetId, `it is ${Math.round(buffer.duration)} s long; samples are limited to ${MAX_SAMPLE_SECONDS} s.`);
      }
      if (generation === this.generation) this.store(assetId, buffer);
      return buffer;
    }).then(
      (buffer) => {
        // Only the request that is still current may update the bookkeeping: after
        // clear()/invalidate() a newer decode for the same asset may be pending.
        if (this.pending.get(assetId) === promise) {
          this.pending.delete(assetId);
          this.errors.delete(assetId);
        }
        return buffer;
      },
      (error: unknown) => {
        const loadError =
          error instanceof AssetLoadError ? error : new AssetLoadError('decode', assetId, `the file could not be read (${String(error)}).`);
        if (this.pending.get(assetId) === promise) {
          this.pending.delete(assetId);
          this.errors.set(assetId, loadError);
        }
        throw loadError;
      },
    );
    this.pending.set(assetId, promise);
    return promise;
  }

  private async withSlot<T>(task: () => Promise<T>): Promise<T> {
    while (this.running >= this.concurrency) await new Promise<void>((resolve) => this.waiters.push(resolve));
    this.running++;
    try {
      return await task();
    } finally {
      this.running--;
      this.waiters.shift()?.();
    }
  }

  private reverse(source: B): B {
    const out = this.deps.create(source.numberOfChannels, source.length, source.sampleRate);
    for (let c = 0; c < source.numberOfChannels; c++) {
      const from = source.getChannelData(c);
      const to = out.getChannelData(c);
      for (let i = 0, n = from.length; i < n; i++) to[i] = from[n - 1 - i]!;
    }
    return out;
  }

  private touch(k: string, entry: Entry<B>): void {
    this.entries.delete(k);
    this.entries.set(k, entry);
  }

  private store(k: string, buffer: B): void {
    const previous = this.entries.get(k);
    if (previous) this.bytes -= previous.bytes;
    const entry = { buffer, bytes: bytesOf(buffer) };
    this.entries.set(k, entry);
    this.bytes += entry.bytes;
    this.evict();
  }

  private evict(): void {
    for (const [k, entry] of this.entries) {
      if (this.bytes <= this.budgetBytes) return;
      if (this.pins.has(k)) continue;
      this.entries.delete(k);
      this.bytes -= entry.bytes;
      this.counters.evictions++;
    }
  }
}

/** Duration, channels, rate and peak of decoded audio (stored on the asset). */
export function analyseBuffer(buffer: DecodedBuffer): AssetAudioInfo {
  let peak = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < data.length; i++) {
      const v = Math.abs(data[i]!);
      if (v > peak) peak = v;
    }
  }
  return {
    durationSec: Math.round(buffer.duration * 1000) / 1000,
    channels: Math.min(32, buffer.numberOfChannels),
    sampleRate: Math.round(buffer.sampleRate),
    peak: Math.min(1, Math.round(peak * 10000) / 10000),
  };
}

export interface Peaks {
  /** Per-bucket minimum and maximum across channels, in [−1, 1]. */
  min: Float32Array;
  max: Float32Array;
  /** Seconds of audio per bucket. */
  secondsPerBucket: number;
}

export const MAX_PEAK_BUCKETS = 1024;

/**
 * Downsampled min/max envelope of a region for drawing. Work is bounded: at most
 * `MAX_PEAK_BUCKETS` buckets, and each bucket reads at most 256 evenly spaced samples
 * per channel, so long files cost the same to draw as short ones.
 */
export function computePeaks(buffer: DecodedBuffer, buckets: number, fromSec = 0, toSec = buffer.duration): Peaks {
  const n = Math.max(1, Math.min(MAX_PEAK_BUCKETS, Math.floor(buckets)));
  const min = new Float32Array(n);
  const max = new Float32Array(n);
  const rate = buffer.sampleRate;
  const start = Math.max(0, Math.floor(fromSec * rate));
  const end = Math.min(buffer.length, Math.ceil(toSec * rate));
  const span = Math.max(0, end - start);
  const per = span / n;
  const stride = Math.max(1, Math.floor(per / 256));
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let b = 0; b < n; b++) {
      const s0 = start + Math.floor(b * per);
      const s1 = Math.max(s0 + 1, Math.min(end, start + Math.floor((b + 1) * per)));
      let lo = c === 0 ? 1 : min[b]!;
      let hi = c === 0 ? -1 : max[b]!;
      for (let i = s0; i < s1; i += stride) {
        const v = data[i] ?? 0;
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
      min[b] = lo;
      max[b] = hi;
    }
  }
  if (span === 0) {
    min.fill(0);
    max.fill(0);
  }
  return { min, max, secondsPerBucket: span / rate / n };
}

/** Browser decoder: an OfflineAudioContext used only for decoding (resamples to 44.1 kHz). */
export function browserDecodeDeps(load: CacheDeps['load']): CacheDeps {
  let decoder: OfflineAudioContext | null = null;
  return {
    load,
    decode: (data) => {
      decoder ??= new OfflineAudioContext(1, 1, DECODE_SAMPLE_RATE);
      return decoder.decodeAudioData(data);
    },
    create: (channels, length, sampleRate) => new AudioBuffer({ numberOfChannels: channels, length, sampleRate }),
  };
}
